import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Heart, Repeat2, ExternalLink, Loader2 } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useState } from 'react';
import { extractThumbnailFromImeta, getVideoEmbedUrl } from '@/lib/videoMetadata';

interface VideoDetailPageProps {
  eventId: string;
}

export function VideoDetailPage({ eventId }: VideoDetailPageProps) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutate: publishEvent } = useNostrPublish();
  const { toast } = useToast();
  const [isLiking, setIsLiking] = useState(false);
  const [isReposting, setIsReposting] = useState(false);

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['video-detail', eventId],
    queryFn: async ({ signal }) => {
      if (!eventId) return null;
      const events = await nostr.query(
        [{ kinds: [21], ids: [eventId], limit: 1 }],
        { signal }
      );
      return events[0] ?? null;
    },
    enabled: !!eventId,
  });

  // Extract metadata using proper helper functions
  const title = event?.tags.find(([name]) => name === 'title')?.[1] ?? 'Untitled Video';
  const imetaTag = event?.tags.find(([name]) => name === 'imeta');
  const imetaContent = imetaTag?.[1] ?? '';
  const videoUrl = imetaContent.match(/url\s+(.+?)(?:\s+image\s+|\s+m\s+|\s*$)/)?.[1];
  // Use getVideoEmbedUrl to properly handle all URL formats (watch, embed, youtu.be, etc.)
  const embedUrl = videoUrl ? getVideoEmbedUrl(videoUrl) : null;
  const imageUrl = extractThumbnailFromImeta(imetaContent);
  const duration = event?.tags.find(([name]) => name === 'duration')?.[1];
  const tags = event?.tags.filter(([name]) => name === 't').map(([, tag]) => tag) ?? [];
  const description = event?.content ?? '';

  const author = useAuthor(event?.pubkey ?? '');
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? (event ? genUserName(event.pubkey) : '');
  const profileImage = metadata?.picture;

  const handleLike = () => {
    if (!user || !event) return;
    setIsLiking(true);
    publishEvent(
      {
        kind: 7,
        content: '+',
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
        ],
      },
      {
        onSuccess: () => {
          toast({ title: 'Liked!', description: 'You liked this video.' });
          setIsLiking(false);
        },
        onError: () => {
          toast({ title: 'Error', description: 'Failed to like.', variant: 'destructive' });
          setIsLiking(false);
        },
      }
    );
  };

  const handleRepost = () => {
    if (!user || !event) return;
    setIsReposting(true);
    publishEvent(
      {
        kind: 6,
        content: '',
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
        ],
      },
      {
        onSuccess: () => {
          toast({ title: 'Reposted!', description: 'You reposted this video.' });
          setIsReposting(false);
        },
        onError: () => {
          toast({ title: 'Error', description: 'Failed to repost.', variant: 'destructive' });
          setIsReposting(false);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Skeleton className="aspect-video w-full rounded-lg mb-6" />
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-4 w-1/2 mb-2" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-destructive">Video not found.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Video Player / Thumbnail */}
      <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-6 relative group">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title={title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : imageUrl ? (
          <img src={sanitizeUrl(imageUrl)} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-muted-foreground">No video preview available</p>
          </div>
        )}
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold mb-4">{title}</h1>

      {/* Author Info */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          {profileImage ? (
            <img src={sanitizeUrl(profileImage)} alt={displayName} className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-cyan-400" />
          )}
          <div>
            <p className="font-semibold">{displayName}</p>
            <p className="text-sm text-muted-foreground">
              {duration && `${Math.floor(Number(duration) / 60)}:${(Number(duration) % 60).toString().padStart(2, '0')}`}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLike}
            disabled={!user || isLiking}
            className="border-pink-500/30 hover:bg-pink-500/10"
          >
            {isLiking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4 text-pink-500" />}
            <span className="ml-2">Like</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRepost}
            disabled={!user || isReposting}
            className="border-cyan-500/30 hover:bg-cyan-500/10"
          >
            {isReposting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat2 className="h-4 w-4 text-cyan-400" />}
            <span className="ml-2">Repost</span>
          </Button>
          {videoUrl && (
            <Button variant="outline" size="sm" asChild className="border-border">
              <a href={sanitizeUrl(videoUrl)} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Description */}
      {description && (
        <Card className="p-4 mb-6 border-border/50">
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{description}</p>
        </Card>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-3 py-1 rounded-full bg-pink-500/10 text-pink-500 border border-pink-500/20"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}